import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { devWarn } from "@/lib/logger";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SiteGate — checks whether the browser has already passed the site-wide
 * HTTP Basic Auth prompt (signed cookie present + valid). If not, redirects
 * the top-level window to /api/gate/enter which returns 401 + WWW-Authenticate
 * so the browser shows its native popup.
 */
export default function SiteGate({ children }) {
  const [state, setState] = useState({ checking: true, passed: false });

  // Runs exactly once on mount — the gate check is a one-shot bootstrap probe,
  // not a reactive effect.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/gate/status`, {
          withCredentials: true,
        });
        if (cancelled) return;
        if (!data?.enabled) {
          setState({ checking: false, passed: true });
          return;
        }
        if (data.passed) {
          setState({ checking: false, passed: true });
          return;
        }
        const nextTarget = window.location.pathname + window.location.search;
        window.location.replace(
          `${BACKEND_URL}/api/gate/enter?next=${encodeURIComponent(nextTarget)}`
        );
      } catch (err) {
        // If the status endpoint itself fails we still want the user to see something.
        devWarn("Site gate status failed", err?.message);
        if (!cancelled) setState({ checking: false, passed: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.checking || !state.passed) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center bg-slate-50 text-slate-500"
        data-testid="site-gate-loader"
      >
        <Loader2 className="h-7 w-7 animate-spin mb-3" />
        <p className="text-sm">Verifying site access…</p>
      </div>
    );
  }

  return children;
}
